import {
    antennaJsonCompensationAntennasDataAntennaIdJsonGet,
    baselineStartDevicesBaselineStartPost,
    baselineStopDevicesBaselineStopPost,
    deleteSweepArchiveAllDevicesDeviceIdArchiveDeleteModeAllDelete,
    deleteSweepArchiveFileDevicesDeviceIdArchiveDeleteFilenameDelete,
    disableNfCompensationCompensationDeviceIdNoiseFigureDisablePost,
    disableTemperatureCompensationCompensationDeviceIdUseRfTemperatureDisablePost,
    disableVswrCompensationCompensationDeviceIdVswrDisablePost,
    enableNfCompensationCompensationDeviceIdNoiseFigureEnablePost,
    enableTemperatureCompensationCompensationDeviceIdUseRfTemperatureEnablePost,
    enableVswrCompensationCompensationDeviceIdVswrEnablePost,
    engineStartAllDevicesAllStartPost,
    engineStartDevicesDeviceIdStartPost,
    engineStopAllDevicesAllStopPost,
    engineStopDevicesDeviceIdStopPost,
    getDeviceAntennasCompensationDeviceIdAntennasGet,
    getDeviceBaselineDevicesDeviceIdBaselineGet,
    getDeviceCfgDevicesDeviceIdConfigGet,
    getDeviceDevicesDeviceIdGet,
    getDeviceStatusDevicesDeviceIdStatusGet,
    getIotBindingsIotBindingsGet,
    getIotDacStateIotIdDacPinRefGet,
    getIotDescriptorIotIdDescriptorGet,
    getIotEventsIotIdEventsRecentGet,
    getIotIotGet,
    getLatestFirmwareOtaLatestGet,
    getLayoutMapLayoutGet,
    iotRelayOffIotIdRelayPinRefOffPost,
    iotRelayOnIotIdRelayPinRefOnPost,
    iotRelaysOnIotIdRelaysOnPost,
    listBaselineFilesDevicesDeviceIdBaselineListGet,
    listDevicesDevicesGet,
    listMeasurementReportsDocumentsMeasurementReportsListGet,
    listSweepArchivesDevicesDeviceIdArchiveModeListGet,
    liveSubscribeDevicesDeviceIdLiveSubscribePost,
    liveUnsubscribeDevicesDeviceIdLiveUnsubscribePost,
    loadBaselineFileDevicesDeviceIdBaselineLoadFilenamePost,
    manifestCompensationAntennasManifestGet,
    otaPushAllOtaPushPost,
    removeIotBindingIotBindingsIdPinIdDelete,
    restartAllDevicesDevicesAllRestartPost,
    restartDeviceDevicesDeviceIdRestartPost,
    saveIotBindingIotBindingsIdPinIdPut,
    saveLayoutMapLayoutPost,
    saveStartAllDevicesAllSaveStartPost,
    saveStartDevicesDeviceIdSaveStartPost,
    saveStopAllDevicesAllSaveStopPost,
    saveStopDevicesDeviceIdSaveStopPost,
    sdrLiveStartDevicesDeviceIdLiveStartPost,
    sdrLiveStopDevicesDeviceIdLiveStopPost,
    setIotDacValueIotIdDacPinRefPost,
    switchDeviceModeDevicesDeviceIdModeModePost,
    updateDeviceCfgDevicesDeviceIdConfigPatch,
    uploadMeasurementReportBundleDocumentsMeasurementReportsUploadBundlePost,
    uploadMeasurementReportDocumentsMeasurementReportsUploadSinglePost,
    zeroIotDacIotIdDacZeroPost,
} from 'gnssgen/client';

import type {
    AntennaData,
    AntennaManifest,
    ArchiveSweepFile,
    BaselinesPayload,
    CenterDeviceConfig,
    CenterDeviceRuntimeStatus,
    CenterDeviceStatus,
    CenterSharedConfig,
    DeviceConfig,
    DeviceMode,
    DeviceStatus,
    WsDeviceInfo,
} from '@ui/Types/Gnss';

import { getLazyRequiredEnvironmentUrl } from '../../environment';

import type { SDRConfigParams } from 'gnssgen/model';
import type { BodyUploadMeasurementReportBundleDocumentsMeasurementReportsUploadBundlePost } from 'gnssgen/model/bodyUploadMeasurementReportBundleDocumentsMeasurementReportsUploadBundlePost';
import type { BodyUploadMeasurementReportDocumentsMeasurementReportsUploadSinglePost } from 'gnssgen/model/bodyUploadMeasurementReportDocumentsMeasurementReportsUploadSinglePost';

const WS_URL = getLazyRequiredEnvironmentUrl('EXPO_PUBLIC_SDR_WS_HOST', 'https');

interface GnssConfigShared {
    gain_mode: DeviceConfig['gain_mode'];
    rf_bandwidth_mhz: number;
    rx1_gain_db: number;
    rx2_gain_db: number;
}

interface GnssConfigSweep {
    alert_streak_th: number;
    f_start_mhz: number;
    f_step_mhz: number;
    f_stop_mhz: number;
    rx1_antenna_id: string;
    rx2_antenna_id: string;
    sample_rate_mhz: number;
    samples_per_step: number;
    samples_skip_ms: number;
    save_target_bins: number;
    threshold_high_pct: number;
    threshold_low_pct: number;
}

interface GnssConfigCenter {
    center_freq_mhz: number;
    center_sample_rate_mhz?: number;
    dc_block_enabled: boolean;
    fft_averages: number;
    fft_size: number;
    row_interval_ms: number;
    sample_rate_mhz?: number;
    span_mhz?: number;
    window_enabled: boolean;
}

interface GnssResponseConfigSweep {
    device_id: string;
    mode: 'sweep';
    shared: GnssConfigShared;
    sweep: GnssConfigSweep;
}

interface GnssResponseConfigCenter {
    center?: GnssConfigCenter;
    device_id: string;
    mode: 'center';
    shared: GnssConfigShared;
}

interface GnssSweepStatusBody {
    ant_comp: 0 | 1;
    ant1_pts: number;
    ant1_state: number;
    ant2_pts: number;
    ant2_state: number;
    bl_running: 0 | 1;
    bl_sweeps_n: number;
    bl_target_n: number;
    bl_valid: 0 | 1;
    rf_temp_c: number;
    save: 0 | 1;
    save_target_bins: number;
    start: number;
    step: number;
    stop: number;
}

interface GnssResponseStatusSweep {
    act_mode: 'sweep';
    cfg_mode: DeviceMode;
    device_id: string;
    enabled: 0 | 1;
    errs: number;
    is_alert: 0 | 1 | 2;
    live: 0 | 1;
    rows: number;
    sweep: GnssSweepStatusBody;
    version: string;
}

interface GnssResponseStatusCenter {
    act_mode: 'center';
    center?: Partial<CenterDeviceRuntimeStatus>;
    cfg_mode: DeviceMode;
    device_id: string;
    enabled: 0 | 1;
    errs: number;
    is_alert: 0 | 1 | 2;
    live: 0 | 1;
    rows: number;
    version: string;
}

type GnssResponseStatus = GnssResponseStatusSweep | GnssResponseStatusCenter;

interface GnssDeviceRegistryBase {
    act_mode: DeviceMode;
    cfg_mode: DeviceMode;
    connected_for_s: number;
    ctrl_port: number;
    enabled: 0 | 1;
    errs: number;
    id: string;
    ip: string;
    last_hb_ts: number;
    live: 0 | 1;
    live_port: number;
    online: boolean;
    online_since_ts: number;
    restart_required: 0 | 1;
    rows: number;
    version: string;
}

interface GnssDeviceRegistrySweep extends GnssDeviceRegistryBase {
    act_mode: 'sweep';
    ant_comp: 0 | 1;
    ant1_pts: number;
    ant1_state: number;
    ant2_pts: number;
    ant2_state: number;
    bl_port: number;
    bl_running: 0 | 1;
    bl_sweep_n: number;
    bl_target_n: number;
    bl_valid: 0 | 1;
    fstart: number;
    fstep: number;
    fstop: number;
    is_alert: 0 | 1 | 2;
    noise_figure: 0 | 1;
    save: 0 | 1;
    save_stop_at_ts: number;
    temp_used: 0 | 1;
}

interface GnssDeviceRegistryCenter extends GnssDeviceRegistryBase {
    act_mode: 'center';
    center_hz: number;
    center_span_hz: number;
    dc_block: 0 | 1;
    fft_avg_applied: number;
    iq_stream_enabled: 0 | 1;
    vbins: number;
    window: 0 | 1;
}

type GnssDeviceRegistry = GnssDeviceRegistrySweep | GnssDeviceRegistryCenter;
type GnssDeviceRegistryMap = Record<string, GnssDeviceRegistry>;

const SDR_CONFIG_PATCH_KEYS = new Set([
    'sample_rate_mhz',
    'rf_bandwidth_mhz',
    'rx1_gain_db',
    'rx2_gain_db',
    'gain_mode',
    'samples_per_step',
    'samples_skip_ms',
    'f_start_mhz',
    'f_stop_mhz',
    'f_step_mhz',
    'save_bins',
    'threshold_low_pct',
    'threshold_high_pct',
    'alert_streak_th',
    'rx1_antenna_id',
    'rx2_antenna_id',
    'center_freq_mhz',
    'center_sample_rate_mhz',
    'fft_size',
    'fft_averages',
    'window_enabled',
    'dc_block_enabled',
    'row_interval_ms',
]);

interface GnssFetcherErrorLike {
    info?: unknown;
    status?: number;
}

export interface GnssIotRelayDescriptor {
    color?: string;
    gpioPin: number;
    logicalPinId: string;
    ordinal: number;
    references: string[];
}

export interface GnssIotDescriptor {
    dacChannels: Record<string, GnssIotDacChannel>;
    descRev: number | null;
    iotId: string;
    pinKinds: Record<string, string>;
    raw: unknown;
    relayColors: Record<string, string>;
    relayPins: Record<string, number>;
    relays: GnssIotRelayDescriptor[];
}

export interface GnssIotDacChannel {
    color: string | null;
    id: string;
    max: number | null;
    min: number | null;
    raw: unknown;
    type: string | null;
    unit: string | null;
}

export interface GnssIotDeviceInfo {
    connectedForSeconds?: number;
    ctrlPort?: number;
    dacChannels: Record<string, GnssIotDacChannel>;
    dacStates: Record<string, number>;
    descRev: number | null;
    descriptorLoaded: boolean | null;
    descriptorTs?: number;
    iotId: string;
    ipAddress?: string;
    lastEvent: GnssIotEvent | null;
    lastEventTs?: number;
    lastHeartbeatTs?: number;
    lastMsgType: string | null;
    lastSeq: number | null;
    lastStateReason: string | null;
    lastStateTs?: number;
    online: boolean | null;
    onlineSinceTs?: number;
    pinColors: Record<string, string>;
    pinKinds: Record<string, string>;
    raw: unknown;
    relayColors: Record<string, string>;
    relayPins: Record<string, number>;
    relays: GnssIotRelayDescriptor[];
    relayStates: Record<string, number>;
    uptimeMs?: number;
}

export interface GnssIotBinding {
    alt: number | null;
    boundDeviceId: string;
    color: string | null;
    gpio: number | null;
    iotId: string;
    kind: string | null;
    label: string | null;
    lat: number | null;
    lon: number | null;
    pinId: string;
    raw: unknown;
    updatedTs: number | null;
}

export interface GnssIotDacState {
    pinRef: string;
    raw: unknown;
    value: number | null;
}

export interface GnssIotEvent {
    cmdId: number | null;
    dac: number | null;
    dacOk: number | null;
    event: string | null;
    evt_ts: number | null;
    expected: number | null;
    gpio: number | null;
    id: string | null;
    ip: string | null;
    msgType: string | null;
    pinId: string | null;
    raw: unknown;
    reason: string | null;
    seq: number | null;
    timeoutMs: number | null;
    type: string | null;
    uptimeMs: number | null;
    value: number | null;
}

export interface GnssIotRelayCommandResponse {
    cmdId: number | null;
    color: string | null;
    iotId: string | null;
    pinId: string | null;
    raw: unknown;
    response: string | null;
    timeoutSeconds: number | null;
}

export type GnssIotBindingsByDevice = Record<string, Record<string, GnssIotBinding>>;
export type GnssIotDeviceMap = Record<string, GnssIotDeviceInfo>;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function getStringValue(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function getNumberValue(value: unknown): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function normalizeUnixTimestampToSeconds(value: unknown): number | null {
    const timestamp = getNumberValue(value);

    if (timestamp === undefined) {
        return null;
    }

    const absoluteTimestamp = Math.abs(timestamp);

    // Keep normalized event timestamps in seconds.
    if (absoluteTimestamp >= 1_000_000_000_000_000) {
        return timestamp / 1_000_000_000;
    }

    if (absoluteTimestamp >= 1_000_000_000_000) {
        return timestamp / 1000;
    }

    return timestamp;
}

function getBooleanValue(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        if (value === 1) {
            return true;
        }

        if (value === 0) {
            return false;
        }
    }

    return null;
}

function getNumberRecord(value: unknown): Record<string, number> {
    if (!isRecord(value)) {
        return {};
    }

    const record: Record<string, number> = {};

    for (const [key, entry] of Object.entries(value)) {
        const normalizedKey = key.trim();
        const normalizedValue = getNumberValue(entry);

        if (normalizedKey.length > 0 && normalizedValue !== undefined) {
            record[normalizedKey] = normalizedValue;
        }
    }

    return record;
}

function getStringRecord(value: unknown): Record<string, string> {
    if (!isRecord(value)) {
        return {};
    }

    const record: Record<string, string> = {};

    for (const [key, entry] of Object.entries(value)) {
        const normalizedKey = key.trim();
        const normalizedValue = getStringValue(entry);

        if (normalizedKey.length > 0 && normalizedValue) {
            record[normalizedKey] = normalizedValue;
        }
    }

    return record;
}

function getRelayOrdinal(logicalPinId: string, index: number) {
    const match = /(\d+)$/.exec(logicalPinId);

    if (match) {
        const parsed = Number(match[1]);

        if (Number.isInteger(parsed) && parsed > 0) {
            return parsed;
        }
    }

    return index + 1;
}

function compareLogicalPinIds(leftPinId: string, rightPinId: string) {
    const ordinalDelta = getRelayOrdinal(leftPinId, 0) - getRelayOrdinal(rightPinId, 0);

    if (ordinalDelta !== 0) {
        return ordinalDelta;
    }

    return leftPinId.localeCompare(rightPinId);
}

function buildRelayReferences(logicalPinId: string, ordinal: number, color?: string) {
    const references: string[] = [];
    const candidates = [logicalPinId, ordinal.toString(), color];

    for (const candidate of candidates) {
        const value = getStringValue(candidate);

        if (value && !references.includes(value)) {
            references.push(value);
        }
    }

    return references;
}

function buildIotRelayDescriptors(
    relayPins: Record<string, number>,
    relayColors: Record<string, string>,
): GnssIotRelayDescriptor[] {
    return Object.entries(relayPins)
        .sort(([leftPinId], [rightPinId]) => compareLogicalPinIds(leftPinId, rightPinId))
        .map(([logicalPinId, gpioPin], index) => {
            const ordinal = getRelayOrdinal(logicalPinId, index);
            const color = relayColors[logicalPinId];

            return {
                color,
                gpioPin,
                logicalPinId,
                ordinal,
                references: buildRelayReferences(logicalPinId, ordinal, color),
            };
        });
}

function normalizeIotDacChannels(value: unknown): Record<string, GnssIotDacChannel> {
    if (!isRecord(value)) {
        return {};
    }

    const entries: (readonly [string, GnssIotDacChannel])[] = [];

    for (const [channelKey, channelValue] of Object.entries(value)) {
        if (!isRecord(channelValue)) {
            continue;
        }

        const id = getStringValue(channelValue.id) ?? channelKey;

        entries.push([
            channelKey,
            {
                color: getStringValue(channelValue.color) ?? null,
                id,
                max: getNumberValue(channelValue.max) ?? null,
                min: getNumberValue(channelValue.min) ?? null,
                raw: channelValue,
                type: getStringValue(channelValue.type) ?? null,
                unit: getStringValue(channelValue.unit) ?? null,
            },
        ]);
    }

    return Object.fromEntries(entries);
}

function normalizeIotEventEntry(entry: unknown): GnssIotEvent {
    const event = isRecord(entry) ? entry : null;

    return {
        cmdId: getNumberValue(event?.cmd_id) ?? null,
        dac: getNumberValue(event?.dac) ?? null,
        dacOk: getNumberValue(event?.dac_ok) ?? null,
        event: getStringValue(event?.event) ?? null,
        evt_ts: normalizeUnixTimestampToSeconds(event?.evt_ts ?? event?.ts ?? event?.at),
        expected: getNumberValue(event?.expected) ?? null,
        gpio: getNumberValue(event?.gpio) ?? null,
        id: getStringValue(event?.id) ?? null,
        ip: getStringValue(event?.ip) ?? null,
        msgType: getStringValue(event?.msg_type) ?? null,
        pinId: getStringValue(event?.pin_id) ?? null,
        raw: entry,
        reason: getStringValue(event?.reason) ?? null,
        seq: getNumberValue(event?.seq) ?? null,
        timeoutMs: getNumberValue(event?.timeout_ms) ?? null,
        type: getStringValue(event?.type) ?? getStringValue(event?.event) ?? getStringValue(event?.msg_type) ?? null,
        uptimeMs: getNumberValue(event?.uptime_ms) ?? null,
        value: getNumberValue(event?.value) ?? null,
    };
}

function normalizeIotRelayCommandResponse(entry: unknown): GnssIotRelayCommandResponse {
    const response = isRecord(entry) ? entry : null;

    return {
        cmdId: getNumberValue(response?.cmd_id) ?? null,
        color: getStringValue(response?.color) ?? null,
        iotId: getStringValue(response?.iot_id) ?? null,
        pinId: getStringValue(response?.pin_id) ?? null,
        raw: entry,
        response: getStringValue(response?.resp) ?? null,
        timeoutSeconds: getNumberValue(response?.timeout_s) ?? null,
    };
}

function normalizeIotDescriptor(value: unknown, iotId: string): GnssIotDescriptor {
    const descriptor = isRecord(value) ? value : null;
    const dacChannels = normalizeIotDacChannels(descriptor?.dac_channels);
    const pinKinds = getStringRecord(descriptor?.pin_kinds);
    const relayPins = getNumberRecord(descriptor?.relay_pins);
    const relayColors = getStringRecord(descriptor?.relay_colors);

    return {
        dacChannels,
        descRev: getNumberValue(descriptor?.desc_rev) ?? null,
        iotId: getStringValue(descriptor?.id) ?? iotId,
        pinKinds,
        raw: value,
        relayColors,
        relayPins,
        relays: buildIotRelayDescriptors(relayPins, relayColors),
    };
}

function normalizeIotDeviceInfo(value: unknown, iotId: string): GnssIotDeviceInfo {
    const deviceInfo = isRecord(value) ? value : null;
    const dacChannels = normalizeIotDacChannels(deviceInfo?.dac_channels);
    const dacStates = getNumberRecord(deviceInfo?.dac_states);
    const pinColors = getStringRecord(deviceInfo?.pin_colors);
    const pinKinds = getStringRecord(deviceInfo?.pin_kinds);
    const relayPins = getNumberRecord(deviceInfo?.relay_pins);
    const relayColors = getStringRecord(deviceInfo?.relay_colors);
    const relayStates = getNumberRecord(deviceInfo?.relay_states);

    return {
        connectedForSeconds: getNumberValue(deviceInfo?.connected_for_s),
        ctrlPort: getNumberValue(deviceInfo?.ctrl_port),
        dacChannels,
        dacStates,
        descRev: getNumberValue(deviceInfo?.desc_rev) ?? null,
        descriptorLoaded: getBooleanValue(deviceInfo?.descriptor_loaded),
        descriptorTs: getNumberValue(deviceInfo?.descriptor_ts),
        iotId: getStringValue(deviceInfo?.id) ?? iotId,
        ipAddress: getStringValue(deviceInfo?.ip),
        lastEvent: deviceInfo?.last_event ? normalizeIotEventEntry(deviceInfo.last_event) : null,
        lastEventTs: getNumberValue(deviceInfo?.last_event_ts),
        lastHeartbeatTs: getNumberValue(deviceInfo?.last_hb_ts),
        lastMsgType: getStringValue(deviceInfo?.last_msg_type) ?? null,
        lastSeq: getNumberValue(deviceInfo?.last_seq) ?? null,
        lastStateReason: getStringValue(deviceInfo?.last_state_reason) ?? null,
        lastStateTs: getNumberValue(deviceInfo?.last_state_ts),
        online: getBooleanValue(deviceInfo?.online),
        onlineSinceTs: getNumberValue(deviceInfo?.online_since_ts),
        pinColors,
        pinKinds,
        raw: value,
        relayColors,
        relayPins,
        relays: buildIotRelayDescriptors(relayPins, relayColors),
        relayStates,
        uptimeMs: getNumberValue(deviceInfo?.uptime_ms),
    };
}

function normalizeIotDeviceMap(value: unknown): GnssIotDeviceMap {
    if (!isRecord(value)) {
        return {};
    }

    return Object.fromEntries(
        Object.entries(value).map(([iotId, entry]) => [iotId, normalizeIotDeviceInfo(entry, iotId)]),
    );
}

function normalizeIotBindings(value: unknown): GnssIotBindingsByDevice {
    if (!isRecord(value)) {
        return {};
    }

    const bindingsRoot = isRecord(value.saved_bindings) ? value.saved_bindings : value;

    const normalizedByDevice: GnssIotBindingsByDevice = {};

    for (const [iotId, bindingsValue] of Object.entries(bindingsRoot)) {
        const pinsRecord = isRecord(bindingsValue) && isRecord(bindingsValue.pins) ? bindingsValue.pins : bindingsValue;
        const bindingsByPin = isRecord(pinsRecord)
            ? pinsRecord
            : Array.isArray(bindingsValue)
              ? Object.fromEntries(
                    bindingsValue
                        .map((entry, index) => {
                            if (!isRecord(entry)) {
                                return null;
                            }

                            const pinId = getStringValue(entry.pin_id) ?? getStringValue(entry.pinId) ?? String(index);

                            return [pinId, entry] as const;
                        })
                        .filter((entry): entry is readonly [string, Record<string, unknown>] => Boolean(entry)),
                )
              : {};

        const normalizedPins: Record<string, GnssIotBinding> = {};

        for (const [pinId, bindingValue] of Object.entries(bindingsByPin)) {
            if (!isRecord(bindingValue)) {
                continue;
            }

            const location = isRecord(bindingValue.location) ? bindingValue.location : null;

            const boundDeviceId =
                getStringValue(bindingValue.bound_device_id) ?? getStringValue(bindingValue.device_id);

            if (!boundDeviceId) {
                continue;
            }

            normalizedPins[pinId] = {
                alt: getNumberValue(location?.alt) ?? getNumberValue(bindingValue.alt) ?? null,
                boundDeviceId,
                color: getStringValue(bindingValue.color) ?? null,
                gpio: getNumberValue(bindingValue.gpio) ?? null,
                iotId,
                kind: getStringValue(bindingValue.kind) ?? null,
                label: getStringValue(bindingValue.label) ?? null,
                lat: getNumberValue(location?.lat) ?? getNumberValue(bindingValue.lat) ?? null,
                lon: getNumberValue(location?.lon) ?? getNumberValue(bindingValue.lon) ?? null,
                pinId,
                raw: bindingValue,
                updatedTs: getNumberValue(bindingValue.updated_ts) ?? null,
            };
        }

        if (Object.keys(normalizedPins).length > 0) {
            normalizedByDevice[iotId] = normalizedPins;
        }
    }

    return normalizedByDevice;
}

function normalizeIotEvents(value: unknown): GnssIotEvent[] {
    const eventsPayload = Array.isArray(value)
        ? value
        : isRecord(value) && Array.isArray(value.events)
          ? value.events
          : null;

    if (!eventsPayload) {
        return [];
    }

    return eventsPayload.map((entry) => normalizeIotEventEntry(entry));
}

function normalizeIotDacState(value: unknown, pinRef: string): GnssIotDacState {
    const record = isRecord(value) ? value : null;

    return {
        pinRef,
        raw: value,
        value: getNumberValue(record?.value) ?? getNumberValue(record?.[pinRef]) ?? null,
    };
}

function isGnssFetcherErrorLike(value: unknown): value is GnssFetcherErrorLike {
    if (!isRecord(value)) {
        return false;
    }

    if (!('status' in value)) {
        return false;
    }

    return typeof value.status === 'number';
}

const BASELINE_FILENAME_PATTERN = /^baseline_(\d+)-(\d+)-(\d+)-(\d+)-(\d+)\.bin$/;
function parseBaselineFilenameMetadata(filename: string) {
    const match = BASELINE_FILENAME_PATTERN.exec(filename.trim());

    if (!match) {
        return null;
    }

    const [, startHzText, stopHzText, stepHzText, sweepsDoneText, completedAtText] = match;
    const fStartHz = Number(startHzText);
    const fStopHz = Number(stopHzText);
    const fStepHz = Number(stepHzText);
    const sweepsDone = Number(sweepsDoneText);
    const completedAtSeconds = Number(completedAtText);

    if (
        !Number.isFinite(fStartHz) ||
        !Number.isFinite(fStopHz) ||
        !Number.isFinite(fStepHz) ||
        !Number.isFinite(sweepsDone) ||
        !Number.isFinite(completedAtSeconds) ||
        fStartHz < 0 ||
        fStopHz <= fStartHz ||
        fStepHz <= 0 ||
        sweepsDone <= 0 ||
        completedAtSeconds <= 0
    ) {
        return null;
    }

    return {
        bins: Math.max(0, Math.floor((fStopHz - fStartHz) / fStepHz) + 1),
        completed_ns: completedAtSeconds * 1_000_000_000,
        f_start_hz: fStartHz,
        f_step_hz: fStepHz,
        f_stop_hz: fStopHz,
        sweeps_done: sweepsDone,
    };
}

function normalizeGnssBaselineFileEntry(value: unknown): BaselinesPayload | null {
    if (!isRecord(value) || typeof value.filename !== 'string') {
        return null;
    }

    const parsed = parseBaselineFilenameMetadata(value.filename);

    if (!parsed) {
        return null;
    }

    return {
        ...parsed,
        device_id: typeof value.device_id === 'string' ? value.device_id : '',
        rx1: [],
        rx1_ant: '',
        rx2: [],
        rx2_ant: '',
        sdr_at_capture: {
            ant_comp_applied: false,
            gain_mode: 'manual',
            rf_bandwidth_mhz: 0,
            rx1_gain_db: 0,
            rx2_gain_db: 0,
            sample_rate_mhz: 0,
            samples_per_step: 0,
        },
        source_baseline_file: value.filename,
    } satisfies BaselinesPayload;
}

function isDeviceMode(value: unknown): value is DeviceMode {
    return value === 'sweep' || value === 'center';
}

function isLegacyDeviceConfig(value: unknown): value is DeviceConfig {
    return isRecord(value) && typeof value.f_start_mhz === 'number' && typeof value.f_stop_mhz === 'number';
}

function normalizeGnssConfig(value: unknown): DeviceConfig | null {
    if (isLegacyDeviceConfig(value)) {
        return value;
    }

    if (!isRecord(value) || value.mode !== 'sweep' || !isRecord(value.shared) || !isRecord(value.sweep)) {
        return null;
    }

    const config = value as unknown as GnssResponseConfigSweep;
    const shared = config.shared;
    const sweep = config.sweep;

    return {
        alert_streak_th: sweep.alert_streak_th,
        f_start_mhz: sweep.f_start_mhz,
        f_step_mhz: sweep.f_step_mhz,
        f_stop_mhz: sweep.f_stop_mhz,
        gain_mode: shared.gain_mode,
        rf_bandwidth_mhz: shared.rf_bandwidth_mhz,
        rx1_antenna_id: sweep.rx1_antenna_id,
        rx1_gain_db: shared.rx1_gain_db,
        rx2_antenna_id: sweep.rx2_antenna_id,
        rx2_gain_db: shared.rx2_gain_db,
        sample_rate_mhz: sweep.sample_rate_mhz,
        samples_per_step: sweep.samples_per_step,
        samples_skip_ms: sweep.samples_skip_ms,
        save_target_bins: sweep.save_target_bins,
        threshold_high_pct: sweep.threshold_high_pct,
        threshold_low_pct: sweep.threshold_low_pct,
    };
}

function getCenterConfigSampleRateMhz(value: {
    center_sample_rate_mhz?: unknown;
    sample_rate_mhz?: unknown;
    span_mhz?: unknown;
}): number | null {
    if (typeof value.center_sample_rate_mhz === 'number') {
        return value.center_sample_rate_mhz;
    }

    if (typeof value.sample_rate_mhz === 'number') {
        return value.sample_rate_mhz;
    }

    if (typeof value.span_mhz === 'number') {
        return value.span_mhz;
    }

    return null;
}

function isCenterConfig(value: unknown): value is Record<string, unknown> {
    return (
        isRecord(value) &&
        typeof value.center_freq_mhz === 'number' &&
        getCenterConfigSampleRateMhz(value) !== null &&
        typeof value.fft_size === 'number' &&
        typeof value.fft_averages === 'number' &&
        typeof value.window_enabled === 'boolean' &&
        typeof value.dc_block_enabled === 'boolean' &&
        typeof value.row_interval_ms === 'number'
    );
}

function normalizeGnssCenterConfig(value: unknown): CenterDeviceConfig | null {
    if (!isRecord(value) || value.mode !== 'center' || typeof value.device_id !== 'string' || !isRecord(value.shared)) {
        return null;
    }

    const config = value as unknown as GnssResponseConfigCenter;
    const center = config.center;

    if (!isCenterConfig(center)) {
        return null;
    }

    const shared = config.shared as unknown as CenterSharedConfig;
    const centerConfig = center as unknown as GnssConfigCenter;
    const centerSampleRateMhz = getCenterConfigSampleRateMhz(centerConfig);

    if (centerSampleRateMhz === null) {
        return null;
    }

    return {
        center: {
            center_freq_mhz: centerConfig.center_freq_mhz,
            center_sample_rate_mhz: centerSampleRateMhz,
            dc_block_enabled: centerConfig.dc_block_enabled,
            fft_averages: centerConfig.fft_averages,
            fft_size: centerConfig.fft_size,
            row_interval_ms: centerConfig.row_interval_ms,
            window_enabled: centerConfig.window_enabled,
        },
        device_id: value.device_id,
        mode: 'center',
        shared: {
            gain_mode: shared.gain_mode,
            rf_bandwidth_mhz: shared.rf_bandwidth_mhz,
            rx1_gain_db: shared.rx1_gain_db,
            rx2_gain_db: shared.rx2_gain_db,
        },
    };
}

export interface GnssDeviceConfigPayload {
    readonly centerConfig: CenterDeviceConfig | null;
    readonly mode: DeviceMode | null;
    readonly sweepConfig: DeviceConfig | null;
}

function normalizeGnssDeviceConfigPayload(value: unknown): GnssDeviceConfigPayload {
    return {
        centerConfig: normalizeGnssCenterConfig(value),
        mode: isRecord(value) && isDeviceMode(value.mode) ? value.mode : null,
        sweepConfig: normalizeGnssConfig(value),
    };
}

function normalizePatchedDeviceConfig(value: unknown): unknown {
    const sweepConfig = normalizeGnssConfig(value);

    if (sweepConfig) {
        return sweepConfig;
    }

    const centerConfig = normalizeGnssCenterConfig(value);

    if (centerConfig) {
        return centerConfig;
    }

    return value;
}

function assignConfigPatchValue(target: Record<string, unknown>, key: string, value: unknown) {
    if (value === undefined) {
        return;
    }

    const normalizedKey = key === 'save_target_bins' ? 'save_bins' : key;

    if (!SDR_CONFIG_PATCH_KEYS.has(normalizedKey)) {
        return;
    }

    target[normalizedKey] = value;
}

function flattenConfigPatch(update: Record<string, unknown>): Record<string, unknown> {
    const flattened: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(update)) {
        if ((key === 'shared' || key === 'sweep') && isRecord(value)) {
            for (const [nestedKey, nestedValue] of Object.entries(value)) {
                assignConfigPatchValue(flattened, nestedKey, nestedValue);
            }

            continue;
        }

        if (key === 'center' && isRecord(value)) {
            for (const [nestedKey, nestedValue] of Object.entries(value)) {
                const normalizedNestedKey =
                    nestedKey === 'sample_rate_mhz' || nestedKey === 'span_mhz' ? 'center_sample_rate_mhz' : nestedKey;

                assignConfigPatchValue(flattened, normalizedNestedKey, nestedValue);
            }

            continue;
        }

        assignConfigPatchValue(flattened, key, value);
    }

    return flattened;
}

function normalizeGnssStatus(value: unknown): DeviceStatus | null {
    if (!isRecord(value)) {
        return null;
    }

    const actMode = value.act_mode;

    if (actMode !== 'sweep' && actMode !== 'center') {
        return value as DeviceStatus;
    }

    const status = value as unknown as GnssResponseStatus;
    const baseStatus: DeviceStatus = {
        act_mode: status.act_mode,
        cfg_mode: status.cfg_mode,
        device_id: status.device_id,
        enabled: status.enabled,
        errs: status.errs,
        is_alert: status.is_alert,
        live: status.live,
        rows: status.rows,
        version: status.version,
    };

    if (status.act_mode !== 'sweep' || !isRecord(status.sweep)) {
        return baseStatus;
    }

    return {
        ...baseStatus,
        ant_comp: status.sweep.ant_comp,
        ant1_pts: status.sweep.ant1_pts,
        ant1_state: status.sweep.ant1_state,
        ant2_pts: status.sweep.ant2_pts,
        ant2_state: status.sweep.ant2_state,
        bl_running: status.sweep.bl_running,
        bl_sweeps_n: status.sweep.bl_sweeps_n,
        bl_target_n: status.sweep.bl_target_n,
        bl_valid: status.sweep.bl_valid,
        rf_temp_c: status.sweep.rf_temp_c,
        save: status.sweep.save,
        save_target_bins: status.sweep.save_target_bins,
        start: status.sweep.start,
        step: status.sweep.step,
        stop: status.sweep.stop,
    };
}

function normalizeGnssCenterStatus(value: unknown): CenterDeviceStatus | null {
    if (!isRecord(value) || value.act_mode !== 'center' || typeof value.device_id !== 'string') {
        return null;
    }

    const centerStatus = value as unknown as GnssResponseStatusCenter;
    const centerRecord = centerStatus.center ?? null;

    return {
        act_mode: 'center',
        center: {
            center_hz: typeof centerRecord?.center_hz === 'number' ? centerRecord.center_hz : undefined,
            center_span_hz: typeof centerRecord?.center_span_hz === 'number' ? centerRecord.center_span_hz : undefined,
            dc_block: typeof centerRecord?.dc_block === 'number' ? centerRecord.dc_block : undefined,
            e_rf_bandwidth_hz:
                typeof centerRecord?.e_rf_bandwidth_hz === 'number' ? centerRecord.e_rf_bandwidth_hz : undefined,
            e_sample_rate_hz:
                typeof centerRecord?.e_sample_rate_hz === 'number' ? centerRecord.e_sample_rate_hz : undefined,
            fft_avg_applied:
                typeof centerRecord?.fft_avg_applied === 'number' ? centerRecord.fft_avg_applied : undefined,
            interval_ms: typeof centerRecord?.interval_ms === 'number' ? centerRecord.interval_ms : undefined,
            view_bins: typeof centerRecord?.view_bins === 'number' ? centerRecord.view_bins : undefined,
            window: typeof centerRecord?.window === 'number' ? centerRecord.window : undefined,
        },
        cfg_mode: centerStatus.cfg_mode,
        device_id: centerStatus.device_id,
        enabled: centerStatus.enabled,
        errs: centerStatus.errs,
        is_alert: centerStatus.is_alert,
        live: centerStatus.live,
        rows: centerStatus.rows,
        version: centerStatus.version,
    };
}

function normalizeGnssDevice(device: GnssDeviceRegistry): WsDeviceInfo {
    const baseDevice: WsDeviceInfo = {
        act_mode: device.act_mode,
        cfg_mode: device.cfg_mode,
        connected_for_s: device.connected_for_s,
        control_port: device.ctrl_port,
        enabled: device.enabled,
        errs: device.errs,
        id: device.id,
        ip: device.ip,
        last_hb_ts: device.last_hb_ts,
        live: device.live,
        live_port: device.live_port,
        online: device.online,
        online_since_ts: device.online_since_ts,
        restart_required: device.restart_required,
        rows: device.rows,
        version: device.version,
    };

    if (device.act_mode !== 'sweep') {
        return {
            ...baseDevice,
            center_hz: device.center_hz,
            center_span_hz: device.center_span_hz,
            dc_block: device.dc_block,
            fft_avg_applied: device.fft_avg_applied,
            iq_stream_enabled: device.iq_stream_enabled,
            vbins: device.vbins,
            window: device.window,
        };
    }

    return {
        ...baseDevice,
        ant_comp: device.ant_comp,
        ant1_pts: device.ant1_pts,
        ant1_state: device.ant1_state,
        ant2_pts: device.ant2_pts,
        ant2_state: device.ant2_state,
        baseline_port: device.bl_port,
        bl_running: device.bl_running,
        bl_sweep_n: device.bl_sweep_n,
        bl_target_n: device.bl_target_n,
        bl_valid: device.bl_valid,
        is_alert: device.is_alert,
        noise_figure: device.noise_figure,
        save: device.save,
        save_stop_at_ts: device.save_stop_at_ts,
        start: device.fstart,
        step: device.fstep,
        stop: device.fstop,
        temp_used: device.temp_used,
    };
}

function normalizeGnssDeviceEntry(value: unknown): WsDeviceInfo | null {
    if (!isRecord(value) || typeof value.id !== 'string' || !isDeviceMode(value.act_mode)) {
        return null;
    }

    return normalizeGnssDevice(value as unknown as GnssDeviceRegistry);
}

function normalizeGnssDeviceMap(value: unknown): Record<string, WsDeviceInfo> | null {
    if (!isRecord(value)) {
        return null;
    }

    return Object.fromEntries(
        Object.entries(value as GnssDeviceRegistryMap).map(([deviceId, device]) => [
            deviceId,
            normalizeGnssDevice(device),
        ]),
    );
}

function normalizeGnssBaselinePayload(value: unknown): BaselinesPayload | null {
    if (
        !isRecord(value) ||
        typeof value.device_id !== 'string' ||
        typeof value.bins !== 'number' ||
        typeof value.completed_ns !== 'number' ||
        typeof value.f_start_hz !== 'number' ||
        typeof value.f_step_hz !== 'number' ||
        typeof value.f_stop_hz !== 'number' ||
        typeof value.rx1_ant !== 'string' ||
        typeof value.rx2_ant !== 'string' ||
        typeof value.sweeps_done !== 'number' ||
        !Array.isArray(value.rx1) ||
        !Array.isArray(value.rx2) ||
        !isRecord(value.sdr_at_capture)
    ) {
        return null;
    }

    const sdrAtCapture = value.sdr_at_capture;

    if (
        typeof sdrAtCapture.rf_bandwidth_mhz !== 'number' ||
        typeof sdrAtCapture.rx1_gain_db !== 'number' ||
        typeof sdrAtCapture.rx2_gain_db !== 'number' ||
        typeof sdrAtCapture.sample_rate_mhz !== 'number' ||
        typeof sdrAtCapture.samples_per_step !== 'number' ||
        typeof sdrAtCapture.ant_comp_applied !== 'boolean' ||
        (sdrAtCapture.gain_mode !== 'manual' && sdrAtCapture.gain_mode !== 'slow_attack')
    ) {
        return null;
    }

    const thresholds =
        isRecord(value.thresholds) &&
        typeof value.thresholds.th_pct_low === 'number' &&
        typeof value.thresholds.th_pct_high === 'number' &&
        typeof value.thresholds.alert_streak === 'number'
            ? {
                  alert_streak: value.thresholds.alert_streak,
                  th_pct_high: value.thresholds.th_pct_high,
                  th_pct_low: value.thresholds.th_pct_low,
              }
            : undefined;

    return {
        bins: value.bins,
        completed_ns: value.completed_ns,
        device_id: value.device_id,
        f_start_hz: value.f_start_hz,
        f_step_hz: value.f_step_hz,
        f_stop_hz: value.f_stop_hz,
        rx1: value.rx1.filter((entry): entry is number => typeof entry === 'number'),
        rx1_ant: value.rx1_ant,
        rx2: value.rx2.filter((entry): entry is number => typeof entry === 'number'),
        rx2_ant: value.rx2_ant,
        sdr_at_capture: {
            ant_comp_applied: sdrAtCapture.ant_comp_applied,
            gain_mode: sdrAtCapture.gain_mode,
            rf_bandwidth_mhz: sdrAtCapture.rf_bandwidth_mhz,
            rx1_gain_db: sdrAtCapture.rx1_gain_db,
            rx2_gain_db: sdrAtCapture.rx2_gain_db,
            sample_rate_mhz: sdrAtCapture.sample_rate_mhz,
            samples_per_step: sdrAtCapture.samples_per_step,
        },
        source_baseline_file:
            typeof value.source_baseline_file === 'string'
                ? value.source_baseline_file
                : typeof value.filename === 'string'
                  ? value.filename
                  : null,
        sweeps_done: value.sweeps_done,
        thresholds,
    } satisfies BaselinesPayload;
}

function normalizeGnssBaselineList(value: unknown): BaselinesPayload[] | null {
    const entries = Array.isArray(value)
        ? value
        : isRecord(value) && Array.isArray(value.files)
          ? value.files
          : isRecord(value) && Array.isArray(value.baselines)
            ? value.baselines
            : null;

    if (!entries) {
        return null;
    }

    return entries.flatMap((entry) => {
        const normalizedDirect = normalizeGnssBaselinePayload(entry);

        if (normalizedDirect) {
            return [normalizedDirect];
        }

        const normalizedFileEntry = normalizeGnssBaselineFileEntry(entry);

        if (normalizedFileEntry) {
            return [normalizedFileEntry];
        }

        if (!isRecord(entry) || !('baseline' in entry)) {
            return [];
        }

        const normalizedNestedBaseline = normalizeGnssBaselinePayload(entry.baseline);

        if (!normalizedNestedBaseline) {
            return [];
        }

        return [
            {
                ...normalizedNestedBaseline,
                source_baseline_file:
                    typeof entry.source_baseline_file === 'string'
                        ? entry.source_baseline_file
                        : typeof entry.filename === 'string'
                          ? entry.filename
                          : (normalizedNestedBaseline.source_baseline_file ?? null),
            },
        ];
    });
}

function normalizeSweepArchiveFiles(value: unknown): ArchiveSweepFile[] | null {
    const entries = Array.isArray(value) ? value : isRecord(value) && Array.isArray(value.files) ? value.files : null;

    if (!entries) {
        return null;
    }

    return entries.flatMap((entry) => {
        if (!isRecord(entry) || typeof entry.filename !== 'string') {
            return [];
        }

        return [
            {
                filename: entry.filename,
                size_bytes: typeof entry.size_bytes === 'number' ? entry.size_bytes : undefined,
            },
        ];
    });
}

export async function switchDeviceMode(deviceSerial: string, mode: DeviceMode) {
    try {
        const data = await switchDeviceModeDevicesDeviceIdModeModePost(deviceSerial, mode);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function startBaselineBulk(deviceSerials: string[], sweeps: number) {
    try {
        const normalized = Math.max(1, Number.isFinite(sweeps) ? Math.floor(sweeps) : 1);
        const data = await baselineStartDevicesBaselineStartPost({
            devices: deviceSerials.map((device_id) => ({ device_id, sweeps: normalized })),
        });

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function stopBaselineBulk(deviceSerials: string[]) {
    try {
        const data = await baselineStopDevicesBaselineStopPost({ devices: deviceSerials });

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function setThresholds(deviceSerial: string, params: { alert_streak: number; high: number; low: number }) {
    try {
        const data = await updateDeviceCfgDevicesDeviceIdConfigPatch(deviceSerial, {
            alert_streak_th: params.alert_streak,
            threshold_high_pct: params.high,
            threshold_low_pct: params.low,
        });

        return { data: normalizePatchedDeviceConfig(data), ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function patchDeviceConfig(deviceSerial: string, update: Record<string, unknown>) {
    try {
        const payload = flattenConfigPatch(update) as SDRConfigParams;
        const data = await updateDeviceCfgDevicesDeviceIdConfigPatch(deviceSerial, payload);

        return { data: normalizePatchedDeviceConfig(data), ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getDeviceConfig(
    deviceSerial: string,
): Promise<{ data: GnssDeviceConfigPayload; ok: boolean } | { error: 'network'; ok: false }> {
    try {
        const response = await getDeviceCfgDevicesDeviceIdConfigGet(deviceSerial);
        const data = normalizeGnssDeviceConfigPayload(response);

        return {
            data: data ?? {
                centerConfig: null,
                mode: null,
                sweepConfig: null,
            },
            ok: true,
        } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getDevices(): Promise<
    { data: Record<string, WsDeviceInfo> | null; ok: boolean } | { error: 'network'; ok: false }
> {
    try {
        const response = await listDevicesDevicesGet();
        const data = normalizeGnssDeviceMap(response);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getDevice(
    deviceSerial: string,
): Promise<{ data: WsDeviceInfo | null; ok: boolean } | { error: 'network'; ok: false }> {
    try {
        const response = await getDeviceDevicesDeviceIdGet(deviceSerial);
        const data = normalizeGnssDeviceEntry(response);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getDeviceStatus(
    deviceSerial: string,
): Promise<{ data: DeviceStatus | null; ok: boolean } | { error: 'network'; ok: false }> {
    try {
        const response = await getDeviceStatusDevicesDeviceIdStatusGet(deviceSerial);
        const data = normalizeGnssStatus(response);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getDeviceStatusSnapshot(deviceSerial: string): Promise<
    | {
          data: {
              actMode: DeviceMode | null;
              centerStatus: CenterDeviceStatus | null;
              cfgMode: DeviceMode | null;
              sweepStatus: DeviceStatus | null;
          };
          ok: boolean;
      }
    | { error: 'network'; ok: false }
> {
    try {
        const response = await getDeviceStatusDevicesDeviceIdStatusGet(deviceSerial);
        let actMode: DeviceMode | null = null;
        let centerStatus: CenterDeviceStatus | null = null;
        let cfgMode: DeviceMode | null = null;
        let sweepStatus: DeviceStatus | null = null;

        if (isRecord(response)) {
            cfgMode = isDeviceMode(response.cfg_mode) ? response.cfg_mode : null;
            actMode = isDeviceMode(response.act_mode) ? response.act_mode : null;
            sweepStatus = actMode === 'sweep' ? normalizeGnssStatus(response) : null;
        }

        centerStatus = normalizeGnssCenterStatus(response);

        return { data: { actMode, centerStatus, cfgMode, sweepStatus }, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getDeviceCenterStatus(
    deviceSerial: string,
): Promise<{ data: CenterDeviceStatus | null; ok: boolean } | { error: 'network'; ok: false }> {
    try {
        const response = await getDeviceStatusDevicesDeviceIdStatusGet(deviceSerial);
        const data = normalizeGnssCenterStatus(response);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function liveStart(deviceSerial: string, session?: string) {
    try {
        const data = await sdrLiveStartDevicesDeviceIdLiveStartPost(deviceSerial, {
            session: session ?? null,
        });

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function liveStop(deviceSerial: string, session?: string) {
    try {
        const data = await sdrLiveStopDevicesDeviceIdLiveStopPost(deviceSerial, {
            session: session ?? null,
        });

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function subscribeStart(deviceSerial: string, session?: string) {
    try {
        const data = await liveSubscribeDevicesDeviceIdLiveSubscribePost(deviceSerial, {
            session: session ?? null,
        });

        return { data, ok: true, status: 200 } as const;
    } catch {
        return { error: 'network', ok: false, status: null } as const;
    }
}

export async function subscribeStop(deviceSerial: string, session?: string) {
    try {
        const data = await liveUnsubscribeDevicesDeviceIdLiveUnsubscribePost(deviceSerial, {
            session: session ?? null,
        });

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getBaseline(
    deviceSerial: string,
): Promise<{ data: BaselinesPayload | null; ok: boolean } | { error: 'network'; ok: false }> {
    try {
        const response = await getDeviceBaselineDevicesDeviceIdBaselineGet(deviceSerial);
        const data = normalizeGnssBaselinePayload(response);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function loadBaselineFile(
    deviceSerial: string,
    filename: string,
): Promise<{ data: unknown; ok: boolean } | { error: 'network'; ok: false }> {
    try {
        const data = await loadBaselineFileDevicesDeviceIdBaselineLoadFilenamePost(deviceSerial, filename);

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: error.info ?? null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

export async function listBaselines(
    deviceSerial: string,
): Promise<{ data: BaselinesPayload[] | null; ok: boolean } | { error: 'network'; ok: false }> {
    try {
        const response = await listBaselineFilesDevicesDeviceIdBaselineListGet(deviceSerial);
        const data = normalizeGnssBaselineList(response);

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

export async function baselineStart(deviceSerial: string, sweeps?: number) {
    return startBaselineBulk([deviceSerial], typeof sweeps === 'number' ? sweeps : 1);
}

export async function baselineStop(deviceSerial: string) {
    return stopBaselineBulk([deviceSerial]);
}

// Sweep REST endpoints are not referenced elsewhere; attempt conventional paths and surface failures
export async function startSweep(deviceSerial: string) {
    try {
        const data = await engineStartDevicesDeviceIdStartPost(deviceSerial);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function stopSweep(deviceSerial: string) {
    try {
        const data = await engineStopDevicesDeviceIdStopPost(deviceSerial);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function startSweepBulk() {
    try {
        const data = await engineStartAllDevicesAllStartPost();

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function stopSweepBulk() {
    try {
        const data = await engineStopAllDevicesAllStopPost();

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

// Archive endpoints
export async function listSweepArchives(
    deviceSerial: string,
): Promise<{ data: ArchiveSweepFile[] | null; ok: boolean } | { error: 'network'; ok: false }> {
    try {
        const response = await listSweepArchivesDevicesDeviceIdArchiveModeListGet(deviceSerial, 'sweep');
        const data = normalizeSweepArchiveFiles(response);

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

export async function downloadAllSweepArchives(deviceSerial: string) {
    try {
        const base = WS_URL;
        const resp = await fetch(`${base}/devices/${encodeURIComponent(deviceSerial)}/archive/download/sweep/all`);
        const blob = await resp.blob();
        // Attempt to read filename from Content-Disposition
        const disp = resp.headers.get('Content-Disposition') || resp.headers.get('content-disposition') || '';
        const match = /filename\*=UTF-8''([^;\n]+)|filename="?([^";\n]+)"?/i.exec(disp);
        const filename = match?.[1] || match?.[2] || `sweeps_${deviceSerial}.zip`;
        return { blob, filename, ok: resp.ok } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function downloadSweepArchiveFile(deviceSerial: string, filename: string) {
    try {
        const base = WS_URL;
        const resp = await fetch(
            `${base}/devices/${encodeURIComponent(deviceSerial)}/archive/download/${encodeURIComponent(filename)}`,
        );
        const blob = await resp.blob();
        // Prefer server-provided name, fallback to requested filename
        const disp = resp.headers.get('Content-Disposition') || resp.headers.get('content-disposition') || '';
        const match = /filename\*=UTF-8''([^;\n]+)|filename="?([^";\n]+)"?/i.exec(disp);
        const outName = match?.[1] || match?.[2] || filename;
        return { blob, filename: outName, ok: resp.ok } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export interface MeasurementReportsListResponse {
    files: Record<string, string[]>;
}

function isMeasurementReportsListResponse(value: unknown): value is MeasurementReportsListResponse {
    if (!isRecord(value) || !isRecord(value.files)) {
        return false;
    }

    return Object.values(value.files).every(
        (entry) => Array.isArray(entry) && entry.every((filename) => typeof filename === 'string'),
    );
}

export async function getMeasurementReportsList(): Promise<
    { data: MeasurementReportsListResponse | null; ok: boolean } | { error: 'network'; ok: false }
> {
    try {
        const response = await listMeasurementReportsDocumentsMeasurementReportsListGet();
        const data = isMeasurementReportsListResponse(response) ? response : null;

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

export async function uploadMeasurementReport(file: File, replaceIfExists?: boolean) {
    try {
        const body: BodyUploadMeasurementReportDocumentsMeasurementReportsUploadSinglePost = { file };
        const data = await uploadMeasurementReportDocumentsMeasurementReportsUploadSinglePost(
            body,
            replaceIfExists === undefined ? undefined : { replace_if_exists: replaceIfExists },
        );

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: error.info ?? null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

export async function uploadMeasurementReportBundle(file: File, replaceIfExists?: boolean) {
    try {
        const body: BodyUploadMeasurementReportBundleDocumentsMeasurementReportsUploadBundlePost = { file };
        const data = await uploadMeasurementReportBundleDocumentsMeasurementReportsUploadBundlePost(
            body,
            replaceIfExists === undefined ? undefined : { replace_if_exists: replaceIfExists },
        );

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: error.info ?? null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

export async function downloadMeasurementReportFile(filename: string) {
    try {
        const resp = await fetch(`${WS_URL}/documents/measurement-reports/download/${encodeURIComponent(filename)}`);
        const blob = await resp.blob();
        const disp = resp.headers.get('Content-Disposition') || resp.headers.get('content-disposition') || '';
        const match = /filename\*=UTF-8''([^;\n]+)|filename="?([^";\n]+)"?/i.exec(disp);
        const outName = match?.[1] || match?.[2] || filename;
        return { blob, filename: outName, ok: resp.ok } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function downloadMeasurementReportBundle(date: string) {
    try {
        const resp = await fetch(`${WS_URL}/documents/measurement-reports/download-bundle/${encodeURIComponent(date)}`);
        const blob = await resp.blob();
        const disp = resp.headers.get('Content-Disposition') || resp.headers.get('content-disposition') || '';
        const match = /filename\*=UTF-8''([^;\n]+)|filename="?([^";\n]+)"?/i.exec(disp);
        const filename = match?.[1] || match?.[2] || `measurement_reports_${date}.zip`;
        return { blob, filename, ok: resp.ok } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function downloadMeasurementReportSummary() {
    try {
        const resp = await fetch(`${WS_URL}/documents/download-summary`);
        const blob = await resp.blob();
        const disp = resp.headers.get('Content-Disposition') || resp.headers.get('content-disposition') || '';
        const match = /filename\*=UTF-8''([^;\n]+)|filename="?([^";\n]+)"?/i.exec(disp);
        const filename = match?.[1] || match?.[2] || 'measurement_reports_summary.zip';
        return { blob, filename, ok: resp.ok } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function clearSweepArchives(deviceSerial: string) {
    try {
        const data = await deleteSweepArchiveAllDevicesDeviceIdArchiveDeleteModeAllDelete(deviceSerial, 'sweep');

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: error.info ?? null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

export async function clearSweepArchiveFile(deviceSerial: string, filename: string) {
    try {
        const data = await deleteSweepArchiveFileDevicesDeviceIdArchiveDeleteFilenameDelete(deviceSerial, filename);

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: error.info ?? null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

// Save (record) endpoints
export async function startSaveBulk() {
    try {
        const data = await saveStartAllDevicesAllSaveStartPost();

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: error.info ?? null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

export async function stopSaveBulk() {
    try {
        const data = await saveStopAllDevicesAllSaveStopPost();

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: error.info ?? null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

export async function startSave(deviceSerial: string) {
    try {
        const data = await saveStartDevicesDeviceIdSaveStartPost(deviceSerial);

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: error.info ?? null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

export async function stopSave(deviceSerial: string) {
    try {
        const data = await saveStopDevicesDeviceIdSaveStopPost(deviceSerial);

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: error.info ?? null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

// Restart endpoints
export async function restartAll() {
    try {
        const data = await restartAllDevicesDevicesAllRestartPost();

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: error.info ?? null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

export async function restartDevice(deviceSerial: string) {
    try {
        const data = await restartDeviceDevicesDeviceIdRestartPost(deviceSerial);

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: error.info ?? null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

export async function rebootAll() {
    return restartAll();
}

export async function rebootDevice(deviceSerial: string) {
    return restartDevice(deviceSerial);
}

// Save layout coordinates for devices
export async function saveLayout(reference: string, coords: { device_id: string; x_pct: number; y_pct: number }[]) {
    try {
        const data = await saveLayoutMapLayoutPost({
            coords,
            reference,
        });

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

// Load layout coordinates for devices
export async function getLayout() {
    try {
        const data = await getLayoutMapLayoutGet();

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getIotDevices(): Promise<
    { data: GnssIotDeviceMap; ok: boolean } | { error: 'network'; ok: false }
> {
    try {
        const response = await getIotIotGet();
        const data = normalizeIotDeviceMap(response);

        return { data: data ?? {}, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getIotEvents(
    iotId: string,
    limit = 50,
): Promise<{ data: GnssIotEvent[]; ok: boolean } | { error: 'network'; ok: false }> {
    try {
        const normalizedLimit = Math.min(50, Math.max(1, Math.floor(limit)));
        const response = await getIotEventsIotIdEventsRecentGet(iotId, { limit: normalizedLimit });
        const data = normalizeIotEvents(response);

        return { data: data ?? [], ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getIotDescriptor(
    iotId: string,
): Promise<{ data: GnssIotDescriptor | null; ok: boolean } | { error: 'network'; ok: false }> {
    try {
        const response = await getIotDescriptorIotIdDescriptorGet(iotId);
        const data = normalizeIotDescriptor(response, iotId);

        return { data: data ?? null, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getIotBindings(): Promise<
    { data: GnssIotBindingsByDevice; ok: boolean } | { error: 'network'; ok: false }
> {
    try {
        const response = await getIotBindingsIotBindingsGet();
        const data = normalizeIotBindings(response);

        return { data: data ?? {}, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getIotDacState(
    iotId: string,
    pinRef: string,
): Promise<{ data: GnssIotDacState | null; ok: boolean } | { error: 'network'; ok: false }> {
    try {
        const response = await getIotDacStateIotIdDacPinRefGet(iotId, pinRef);
        const data = normalizeIotDacState(response, pinRef);

        return { data: data ?? null, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

export async function setIotDacValue(iotId: string, pinRef: string, value: number) {
    try {
        const data = await setIotDacValueIotIdDacPinRefPost(iotId, pinRef, { value });

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function zeroIotDac(iotId: string) {
    try {
        const data = await zeroIotDacIotIdDacZeroPost(iotId);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function turnIotRelayOn(iotId: string, pinRef: string, timeoutSeconds: number, username: string) {
    const normalizedTimeout = Math.min(60, Math.max(1, Math.floor(timeoutSeconds)));
    const normalizedUsername = username.trim();

    try {
        const response = await iotRelayOnIotIdRelayPinRefOnPost(iotId, pinRef, {
            timeout_s: normalizedTimeout,
            username: normalizedUsername,
        });
        const data = normalizeIotRelayCommandResponse(response);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function turnIotRelaysOn(
    iotId: string,
    username: string,
    payload: {
        pin_refs: readonly string[];
        timeout_s?: null | number;
        value: number;
    },
) {
    const normalizedUsername = username.trim();

    try {
        const response = await iotRelaysOnIotIdRelaysOnPost(
            iotId,
            {
                pin_refs: payload.pin_refs as string[],
                timeout_s: payload.timeout_s ?? null,
            },
            { username: normalizedUsername },
        );
        const data = normalizeIotRelayCommandResponse(response);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function turnIotRelayOff(iotId: string, pinRef: string, username: string) {
    const normalizedUsername = username.trim();

    try {
        const data = await iotRelayOffIotIdRelayPinRefOffPost(iotId, pinRef, {
            username: normalizedUsername,
        });

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function saveIotBinding(
    iotId: string,
    pinId: string,
    payload: {
        alt?: null | number;
        bound_device_id: string;
        kind?: string | null;
        label?: string | null;
        lat: number;
        lon: number;
    },
) {
    try {
        const data = await saveIotBindingIotBindingsIdPinIdPut(iotId, pinId, {
            ...payload,
            alt: payload.alt ?? 0,
        });

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function removeIotBinding(iotId: string, pinId: string) {
    try {
        const data = await removeIotBindingIotBindingsIdPinIdDelete(iotId, pinId);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

// OTA helpers
export async function fetchFirmware(): Promise<
    { data: Record<string, unknown> | null; ok: boolean } | { error: 'network'; ok: false }
> {
    try {
        const response = await getLatestFirmwareOtaLatestGet();
        const data = isRecord(response) ? (response as Record<string, unknown>) : null;

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function pushFirmware(scope: string, version: string | null) {
    try {
        void scope;
        void version;
        const data = await otaPushAllOtaPushPost();

        return { data, ok: true } as const;
    } catch (error) {
        if (isGnssFetcherErrorLike(error)) {
            return { data: error.info ?? null, ok: false } as const;
        }

        return { error: 'network', ok: false } as const;
    }
}

// Antenna dataset endpoints
export async function getAntennaManifest(): Promise<
    { data: AntennaManifest | null; ok: boolean } | { error: 'network'; ok: false }
> {
    try {
        const data = (await manifestCompensationAntennasManifestGet()) as AntennaManifest;

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getDeviceAntennaManifest(
    deviceSerial: string,
): Promise<{ data: AntennaManifest | null; ok: boolean } | { error: 'network'; ok: false }> {
    try {
        const data = (await getDeviceAntennasCompensationDeviceIdAntennasGet(deviceSerial)) as AntennaManifest;

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function enableVswrCompensation(deviceSerial: string) {
    try {
        const data = await enableVswrCompensationCompensationDeviceIdVswrEnablePost(deviceSerial);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function disableVswrCompensation(deviceSerial: string) {
    try {
        const data = await disableVswrCompensationCompensationDeviceIdVswrDisablePost(deviceSerial);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function enableNoiseFigureCompensation(deviceSerial: string) {
    try {
        const data = await enableNfCompensationCompensationDeviceIdNoiseFigureEnablePost(deviceSerial);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function disableNoiseFigureCompensation(deviceSerial: string) {
    try {
        const data = await disableNfCompensationCompensationDeviceIdNoiseFigureDisablePost(deviceSerial);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function enableTemperatureCompensation(deviceSerial: string) {
    try {
        const data = await enableTemperatureCompensationCompensationDeviceIdUseRfTemperatureEnablePost(deviceSerial);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function disableTemperatureCompensation(deviceSerial: string) {
    try {
        const data = await disableTemperatureCompensationCompensationDeviceIdUseRfTemperatureDisablePost(deviceSerial);

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getAntennaDataJson(
    antennaId: string,
): Promise<{ data: AntennaData | null; ok: boolean } | { error: 'network'; ok: false }> {
    try {
        const data = (await antennaJsonCompensationAntennasDataAntennaIdJsonGet(antennaId)) as AntennaData;

        return { data, ok: true } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export async function getAntennaDataBin(antennaId: string) {
    try {
        const resp = await fetch(`${WS_URL}/compensation/antennas/data/${encodeURIComponent(antennaId)}.bin`);
        const blob = await resp.blob();
        const disp = resp.headers.get('Content-Disposition') || resp.headers.get('content-disposition') || '';
        const match = /filename\*=UTF-8''([^;\n]+)|filename?"?([^";\n]+)"?/i.exec(disp);
        const filename = match?.[1] || match?.[2] || `${antennaId}.bin`;
        return { blob, filename, ok: resp.ok } as const;
    } catch {
        return { error: 'network', ok: false } as const;
    }
}

export {
    fetchFirmware as fetchGnssFirmware,
    getAntennaDataJson as getGnssAntennaDataJson,
    getAntennaManifest as getGnssAntennaManifest,
    getBaseline as getGnssBaseline,
    getDevice as getGnssDevice,
    getDeviceAntennaManifest as getGnssDeviceAntennaManifest,
    getDeviceCenterStatus as getGnssDeviceCenterStatus,
    getDeviceConfig as getGnssDeviceConfig,
    getDevices as getGnssDevices,
    getDeviceStatus as getGnssDeviceStatus,
    getDeviceStatusSnapshot as getGnssDeviceStatusSnapshot,
    getIotBindings as getGnssIotBindings,
    getIotDacState as getGnssIotDacState,
    getIotDescriptor as getGnssIotDescriptor,
    getIotDevices as getGnssIotDevices,
    getIotEvents as getGnssIotEvents,
    getLayout as getGnssLayout,
    getMeasurementReportsList as getGnssMeasurementReportsList,
    baselineStart as gnssBaselineStart,
    baselineStop as gnssBaselineStop,
    clearSweepArchiveFile as gnssClearSweepArchiveFile,
    clearSweepArchives as gnssClearSweepArchives,
    disableNoiseFigureCompensation as gnssDisableNoiseFigureCompensation,
    disableTemperatureCompensation as gnssDisableTemperatureCompensation,
    disableVswrCompensation as gnssDisableVswrCompensation,
    downloadAllSweepArchives as gnssDownloadAllSweepArchives,
    downloadMeasurementReportBundle as gnssDownloadMeasurementReportBundle,
    downloadMeasurementReportFile as gnssDownloadMeasurementReportFile,
    downloadMeasurementReportSummary as gnssDownloadMeasurementReportSummary,
    downloadSweepArchiveFile as gnssDownloadSweepArchiveFile,
    enableNoiseFigureCompensation as gnssEnableNoiseFigureCompensation,
    enableTemperatureCompensation as gnssEnableTemperatureCompensation,
    enableVswrCompensation as gnssEnableVswrCompensation,
    getAntennaDataBin as gnssGetAntennaDataBin,
    liveStart as gnssLiveStart,
    liveStop as gnssLiveStop,
    loadBaselineFile as gnssLoadBaselineFile,
    patchDeviceConfig as gnssPatchDeviceConfig,
    pushFirmware as gnssPushFirmware,
    rebootAll as gnssRebootAll,
    rebootDevice as gnssRebootDevice,
    removeIotBinding as gnssRemoveIotBinding,
    restartAll as gnssRestartAll,
    restartDevice as gnssRestartDevice,
    saveIotBinding as gnssSaveIotBinding,
    saveLayout as gnssSaveLayout,
    setIotDacValue as gnssSetIotDacValue,
    setThresholds as gnssSetThresholds,
    startBaselineBulk as gnssStartBaselineBulk,
    startSave as gnssStartSave,
    startSaveBulk as gnssStartSaveBulk,
    startSweep as gnssStartSweep,
    startSweepBulk as gnssStartSweepBulk,
    stopBaselineBulk as gnssStopBaselineBulk,
    stopSave as gnssStopSave,
    stopSaveBulk as gnssStopSaveBulk,
    stopSweep as gnssStopSweep,
    stopSweepBulk as gnssStopSweepBulk,
    subscribeStart as gnssSubscribeStart,
    subscribeStop as gnssSubscribeStop,
    switchDeviceMode as gnssSwitchDeviceMode,
    turnIotRelayOff as gnssTurnIotRelayOff,
    turnIotRelayOn as gnssTurnIotRelayOn,
    turnIotRelaysOn as gnssTurnIotRelaysOn,
    uploadMeasurementReport as gnssUploadMeasurementReport,
    uploadMeasurementReportBundle as gnssUploadMeasurementReportBundle,
    zeroIotDac as gnssZeroIotDac,
    listSweepArchives as listGnssSweepArchives,
};
